import type { AlgorithmType } from "../redistribution/models/AlgorithmType.js";
import type { DemandSourceType } from "../redistribution/models/DemandSourceType.ts";
import type { DropdownOption } from "../redistribution/models/DropdownOption.js";
import type { PurchaseOrder } from "../redistribution/models/PurchaseOrder.js";
import { apiUrl } from "./apiBase.ts";
import {
  runGetRequestIdByPoNumber,
  runSearch,
  runSearchByPONumbers,
  runSearchByRequestId,
} from "./apiClient.ts";
import {
  buildVendorOptions,
  buildWarehouseOptions,
  getCollectionsCache,
  mapCollectionResponseToOptions,
  setCachedCollections,
  type CollectionResponse,
} from "./optionsBuilders.ts";
import { runRedistribution } from "./redistributionService.ts";
import type { SkuForecastsResult } from "./skuForecasts.ts";
import {
  buildAvgDaysOfStockRequestBody,
  getEarliestArrivalDateString,
  skuForecastCache,
  skuForecastInFlight,
} from "./skuForecasts.ts";

// Re-export for backwards compatibility
export type { SkuForecastsResult } from "./skuForecasts.ts";

/* ── Filter shape for PO search ─────────────────────────────────────────── */

export interface SearchFilters {
  division?: string;
  whsNumber?: string;
  vendor?: string;
  color?: string;
  theme?: string;
  arrivalDateFrom: Date;
  arrivalDateTo: Date;
}

const DATASET_LOG_PREFIX = "[RediSku data]";

function logDatasetFetch(
  event: string,
  details: Record<string, unknown>
): void {
  console.info(`${DATASET_LOG_PREFIX} ${event}`, details);
}

function summarizePurchaseOrdersForLog(
  purchaseOrders: PurchaseOrder[]
): string[] {
  return purchaseOrders
    .map((purchaseOrder) => purchaseOrder.purchaseOrderNumber)
    .filter(
      (purchaseOrderNumber): purchaseOrderNumber is string =>
        typeof purchaseOrderNumber === "string" &&
        purchaseOrderNumber.trim().length > 0
    )
    .slice(0, 25);
}

/* ── Purchase order search ───────────────────────────────────────────────── */

export async function fetchWarehouses(): Promise<DropdownOption[]> {
  const response = await fetch(apiUrl(`/api/PurchaseOrder/warehouse`));
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Fetch Warehouses failed (${String(response.status)}): ${text || response.statusText}`
    );
  }
  const data = (await response.json()) as WarehouseResponse;
  const options = buildWarehouseOptions(data.response);
  logDatasetFetch("warehouses fetched", {
    fetchedCount: data.response.length,
    optionCount: options.length,
    duplicateLabelsRemovedOrDisambiguated:
      data.response.length - options.length,
    sampleOptions: options.slice(0, 10).map((option) => option.label),
  });
  return options;
}
interface WarehouseResponse {
  response: WarehouseResponseRow[];
}
interface WarehouseResponseRow {
  warehouseID: number;
  warehouseName: string;
  division: string;
}

/* ── Vendors ─────────────────────────────────────────────────────────────── */

export async function fetchVendors(): Promise<DropdownOption[]> {
  const response = await fetch(apiUrl(`/api/PurchaseOrder/vendor`));
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Fetch Vendors failed (${String(response.status)}): ${text || response.statusText}`
    );
  }
  const data = (await response.json()) as VendorResponse;
  const options = buildVendorOptions(data.response);
  logDatasetFetch("vendors fetched", {
    fetchedCount: data.response.length,
    optionCount: options.length,
    sampleOptions: options.slice(0, 10).map((option) => option.value),
  });
  return options;
}
interface VendorResponse {
  response: VendorResponseRow[];
}
interface VendorResponseRow {
  vendorCode: string;
  vendorName: string;
}

/* ── Collections ─────────────────────────────────────────────────────────── */
export function getCachedCollections(
  vendorId: string | undefined
): DropdownOption[] {
  if (!vendorId) {
    return [];
  }

  const cache = getCollectionsCache();
  const cachedCollections = cache[vendorId];
  return Array.isArray(cachedCollections) ? cachedCollections : [];
}

export async function fetchCollections(
  vendorId: string
): Promise<DropdownOption[]> {
  const trimmedVendorId = vendorId.trim();
  if (trimmedVendorId === "") {
    return [];
  }
  const response = await fetch(
    apiUrl(
      `/api/PurchaseOrder/collection?${new URLSearchParams({
        VendorCodeSearch: trimmedVendorId,
      }).toString()}`
    )
  );
  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new Error(
      `Fetch Collections failed (${String(response.status)}): ${text || response.statusText}`
    );
  }

  const data = (await response.json()) as CollectionResponse;
  const options = mapCollectionResponseToOptions(data).filter(
    (option) => option.metadata?.vendorId === trimmedVendorId
  );
  logDatasetFetch("collections fetched", {
    vendorId: trimmedVendorId,
    fetchedCount: data.response.length,
    optionCount: options.length,
    sampleOptions: options.slice(0, 10).map((option) => option.value),
  });
  setCachedCollections(trimmedVendorId, options);
  return options;
}
/* ── Purchase order search ───────────────────────────────────────────────── */

export function searchPurchaseOrders(
  filters: SearchFilters
): Promise<PurchaseOrder[]> {
  return runSearch(filters).then((res) => {
    logDatasetFetch("purchase orders searched", {
      filters: {
        division: filters.division,
        whsNumber: filters.whsNumber,
        vendor: filters.vendor,
        color: filters.color,
        theme: filters.theme,
        arrivalDateFrom: filters.arrivalDateFrom.toISOString(),
        arrivalDateTo: filters.arrivalDateTo.toISOString(),
      },
      resultCount: res.response.length,
      purchaseOrderNumbers: summarizePurchaseOrdersForLog(res.response),
    });
    return res.response;
  });
}

export function searchPurchaseOrdersByPONumbers(
  poNumbers: string
): Promise<PurchaseOrder[]> {
  return runSearchByPONumbers(poNumbers).then((res) => {
    logDatasetFetch("purchase orders searched by PO numbers", {
      requestedPoNumbers: poNumbers.split(/\s+/).filter(Boolean),
      resultCount: res.response.length,
      purchaseOrderNumbers: summarizePurchaseOrdersForLog(res.response),
    });
    return res.response;
  });
}

export function searchPurchaseOrdersByRequestId(
  purchaseOrderRequestId: string | number
): Promise<PurchaseOrder[]> {
  return runSearchByRequestId(purchaseOrderRequestId).then((res) => {
    logDatasetFetch("purchase orders searched by request ID", {
      purchaseOrderRequestId: String(purchaseOrderRequestId),
      resultCount: res.response.length,
      purchaseOrderNumbers: summarizePurchaseOrdersForLog(res.response),
    });
    return res.response;
  });
}

export async function fetchRequestIdByPoNumber(
  poNumber: string
): Promise<string | undefined> {
  const result = await runGetRequestIdByPoNumber(poNumber);
  logDatasetFetch("request ID resolved by PO number", {
    poNumber,
    purchaseOrderRequestId: result?.purchaseOrderRequestId,
  });
  return result?.purchaseOrderRequestId;
}

const SKU_FORECAST_CACHE_TTL_MS = 5 * 60 * 1000;

export async function getSkuForecasts(
  avgWOSWarehouses: string[],
  purchaseOrders: PurchaseOrder[],
  rateOfSaleDays: number
): Promise<SkuForecastsResult> {
  if (purchaseOrders.length === 0) {
    return {};
  }

  const arrivalDateStr = getEarliestArrivalDateString(purchaseOrders);
  const requestBody = buildAvgDaysOfStockRequestBody(
    avgWOSWarehouses,
    purchaseOrders,
    rateOfSaleDays,
    arrivalDateStr
  );
  const cacheKey = JSON.stringify(requestBody);

  const now = Date.now();
  const cached = skuForecastCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  skuForecastCache.delete(cacheKey);

  const inFlight = skuForecastInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const fetchPromise = (async (): Promise<SkuForecastsResult> => {
    const response = await fetch(
      apiUrl(`/api/PurchaseOrder/avg-days-of-stock-per-sku`),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      }
    );
    if (!response.ok) {
      throw new Error(
        `Fetch SKU forecasts failed (${String(response.status)}): ${response.statusText}`
      );
    }

    const data = (await response.json()) as SkuForecastsResult;
    skuForecastCache.set(cacheKey, {
      expiresAt: Date.now() + SKU_FORECAST_CACHE_TTL_MS,
      value: data,
    });
    return data;
  })();

  skuForecastInFlight.set(cacheKey, fetchPromise);

  try {
    return await fetchPromise;
  } finally {
    skuForecastInFlight.delete(cacheKey);
  }
}

/* ── Redistribution algorithm ────────────────────────────────────────────── */

export function runRedistributionAlgorithm(
  purchaseOrders: PurchaseOrder[],
  rateOfSaleDays: number,
  poCube: number,
  algorithm: AlgorithmType = "BruteForce",
  allowedReasons?: string[],
  avgDaysOfStockBySku?: SkuForecastsResult,
  demandSource?: DemandSourceType
) {
  return runRedistribution(
    purchaseOrders,
    rateOfSaleDays,
    poCube,
    algorithm,
    allowedReasons,
    avgDaysOfStockBySku,
    demandSource
  );
}
